import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Box,
  Collapse,
  Typography,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  BORDER_RADIUS,
  ANIMATION,
  SCROLLBAR,
  LINE_HEIGHT,
} from "../utils/themeConstants";
import {
  DashboardRounded,
  ChatBubbleRounded,
  PaymentRounded,
  BarChartRounded,
  StorefrontRounded,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import MallInHandLogo from "./MallInHandLogo";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileToggle?: () => void;
}

/**
 * Sidebar Component
 *
 * Enterprise-grade navigation sidebar with smooth animations and professional styling.
 *
 * Features:
 * - Collapsible sidebar with smooth width transitions
 * - Active route highlighting with visual indicators
 * - Hover effects with tooltips for collapsed state
 * - Glass-morphism effects on active items
 * - Responsive animations using Material-UI Collapse
 * - Mobile temporary drawer with backdrop
 * - Optimized re-rendering with React.memo
 */
const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  mobileOpen = false,
  onMobileToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const drawerWidth = 240;
  const collapsedWidth = 64;

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <DashboardRounded />,
      path: "/app/dashboard",
      description: "Overview & Key Metrics",
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: <BarChartRounded />,
      path: "/app/analytics",
      description: "Detailed Insights & Reports",
    },
    {
      id: "messages",
      label: "Messages",
      icon: <ChatBubbleRounded />,
      path: "/app/messages",
      description: "Customer Feedback",
    },
    {
      id: "payments",
      label: "Payments",
      icon: <PaymentRounded />,
      path: "/app/payments",
      description: "Transaction History",
    },
    {
      id: "stores",
      label: "Stores",
      icon: <StorefrontRounded />,
      path: "/app/stores",
      description: "Store Info & Contacts",
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (onMobileToggle) {
      onMobileToggle();
    }
  };

  // Drawer content (shared between mobile and desktop)
  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: 0,
          px: isOpen ? 2 : 1.25,
          borderBottom: "1px solid",
          borderColor: "rgba(0,0,0,0.08)",
          background: "#ffffff",
          color: "text.primary",
          height: { xs: 64, sm: 72 },
          display: "flex",
          alignItems: "center",
          justifyContent: isOpen ? "flex-start" : "center",
          flexShrink: 0,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <MallInHandLogo
          variant={isOpen ? "full" : "icon"}
          width={isOpen ? { xs: 160, sm: 180, md: 200 } : 40}
          color="primary"
        />
      </Box>

      {/* Navigation List */}
      <List
        sx={{ flex: 1, py: 2, px: 1.5, overflowY: "auto", overflowX: "hidden" }}
      >
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <Tooltip
              key={item.id}
              title={!isOpen ? item.label : ""}
              placement="right"
              arrow
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 200 }}
            >
              <ListItem
                disablePadding
                sx={{
                  mb: 0.75,
                }}
              >
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    minHeight: 48,
                    borderRadius: BORDER_RADIUS.MD,
                    pl: isOpen ? 2 : 1.25,
                    pr: isOpen ? 2 : 1.25,
                    justifyContent: "flex-start",
                    background: isActive
                      ? "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                      : "transparent",
                    border: "1px solid transparent",
                    position: "relative",
                    overflow: "hidden",
                    transition: `all ${ANIMATION.NORMAL}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                    boxShadow: isActive
                      ? "0 2px 8px rgba(26, 35, 126, 0.3)"
                      : "none",
                    "&:hover": {
                      background: isActive
                        ? "linear-gradient(135deg, #0d1642 0%, #283593 100%)"
                        : "rgba(26, 35, 126, 0.06)",
                      borderColor: "rgba(26, 35, 126, 0.2)",
                      boxShadow: isActive
                        ? "0 4px 12px rgba(26, 35, 126, 0.4)"
                        : "none",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      width: 24,
                      color: isActive ? "white" : "text.secondary",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: `color ${ANIMATION.NORMAL}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${ANIMATION.NORMAL}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                      transform: isActive ? "scale(1.1)" : "scale(1)",
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <Collapse
                    in={isOpen}
                    orientation="horizontal"
                    timeout={ANIMATION.SLOW}
                  >
                    <Box sx={{ ml: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? "white" : "text.primary",
                          whiteSpace: "nowrap",
                          fontSize: "0.9rem",
                          letterSpacing: "0.3px",
                          lineHeight: LINE_HEIGHT.TIGHT,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isActive
                              ? "rgba(255,255,255,0.9)"
                              : "text.secondary",
                            display: "block",
                            whiteSpace: "nowrap",
                            fontSize: "0.7rem",
                            mt: 0.25,
                            opacity: 0.9,
                            lineHeight: LINE_HEIGHT.SNUG,
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "rgba(0,0,0,0.08)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Collapse in={isOpen} orientation="horizontal" timeout={300}>
          <Box sx={{ textAlign: "center" }}>
            {/* Version info removed for cleaner look */}
          </Box>
        </Collapse>

        {/* Toggle Button - Only show on desktop */}
        <Tooltip
          title={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          placement="right"
          arrow
        >
          <IconButton
            onClick={onToggle}
            size="medium"
            sx={{
              color: "white",
              background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
              border: "none",
              transition: `all ${ANIMATION.NORMAL}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              flexShrink: 0,
              display: { xs: "none", md: "flex" }, // Hide toggle on mobile
              "&:hover": {
                background: "linear-gradient(135deg, #0d1642 0%, #283593 100%)",
                transform: "scale(1.1)",
                boxShadow: "0 4px 16px rgba(26, 35, 126, 0.4)",
              },
            }}
          >
            {isOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer (temporary, with backdrop) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            border: "none",
            background: "#ffffff",
            boxShadow: "8px 0 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer (permanent) */}
      <Drawer
        variant="permanent"
        sx={{
          width: isOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "& .MuiDrawer-paper": {
            width: isOpen ? drawerWidth : collapsedWidth,
            boxSizing: "border-box",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflowX: "hidden",
            overflowY: "auto",
            position: "fixed",
            height: "100vh",
            border: "none",
            borderRight: "1px solid",
            borderColor: "rgba(0,0,0,0.08)",
            borderRadius: 0,
            background: "#ffffff",
            color: "text.primary",
            boxShadow:
              "4px 0 32px rgba(26, 35, 126, 0.08), 2px 0 16px rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: SCROLLBAR.WIDTH,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(26, 35, 126, 0.2)",
              borderRadius: SCROLLBAR.BORDER_RADIUS,
              "&:hover": {
                background: "rgba(26, 35, 126, 0.3)",
              },
            },
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default React.memo(Sidebar);
